// DashboardManagementSection
// Renders two cards:
//   1. "Add + Assign Dashboard" form (handles both create and edit)
//   2. "Configured Dashboards" table
// All state and handlers are owned by ConsoleAdmin.tsx and passed in as props.

import React, { FormEvent } from 'react';
import { ConsoleDashboardSummary, ConsoleUser } from '../../../types';
import { DashboardForm } from './useDashboardForm';

interface DashboardManagementSectionProps {
  // Form state (owned by ConsoleAdmin's useDashboardForm)
  form: DashboardForm;
  onFieldChange: (field: keyof DashboardForm, value: string) => void;
  onSelectExisting: (dashboardId: string) => void; // '' = "Create new dashboard"

  // User Manager Admin — optional, only shows users with tableau_admin role
  // named dashboard-prefixed as tables may have same pattern later (tableUserManagerAdmin)
  activeDashboardUserManagerAdminUsers: ConsoleUser[]; // pre-filtered to tableau_admin role only, filtering done in ConsoleAdmin.tsx

  // Data
  dashboards: ConsoleDashboardSummary[];
  activeAssignableUsers: ConsoleUser[];
  loadingDashboards: boolean;
  dashboardDeleteLoadingId: string;

  // Handlers
  handleUpsertDashboard: (event: FormEvent) => Promise<void>;
  loadDashboardIntoForm: (dashboard: ConsoleDashboardSummary) => void;
  handleToggleDashboardSoftDelete: (row: ConsoleDashboardSummary) => Promise<void>;
}

const DashboardManagementSection: React.FC<DashboardManagementSectionProps> = ({
  form,
  onFieldChange,
  onSelectExisting,
  activeDashboardUserManagerAdminUsers,
  dashboards,
  activeAssignableUsers,
  loadingDashboards,
  dashboardDeleteLoadingId,
  handleUpsertDashboard,
  loadDashboardIntoForm,
  handleToggleDashboardSoftDelete,
}) => {
  return (
    <>
      {/* ── Add + Assign Dashboard Form ── */}
      <div className="console-grid two-col">
        <div className="console-card">
          <h2>Add + Assign Dashboard</h2>
          <form className="console-form" onSubmit={handleUpsertDashboard}>
            <label>
              Existing Dashboard (optional)
              <select
                value={form.dashboardId}
                onChange={(e) => onSelectExisting(e.target.value)}
              >
                <option value="">Create new dashboard</option>
                {dashboards.map((row) => (
                  <option key={row.dashboard_id} value={row.dashboard_id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dashboard Name
              <input
                value={form.name}
                onChange={(e) => onFieldChange('name', e.target.value)}
                placeholder="Core Metrics Analysis"
                required
              />
            </label>
            <label>
              Description
              <textarea
                value={form.description}
                onChange={(e) => onFieldChange('description', e.target.value)}
                rows={4}
                placeholder="Short dashboard summary"
              />
            </label>
            <label>
              Source Link
              <input
                value={form.sourceLink}
                onChange={(e) => onFieldChange('sourceLink', e.target.value)}
                placeholder="https://tableau...."
              />
            </label>
            <label>
              Primary User
              <select
                value={form.primaryDeveloper}
                onChange={(e) => onFieldChange('primaryDeveloper', e.target.value)}
                required
              >
                <option value="">Select user</option>
                {activeAssignableUsers.map((entry) => (
                  <option key={`dashboard-primary-${entry.user_email}`} value={entry.user_email}>
                    {entry.user_email}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Secondary User
              <select
                value={form.secondaryDeveloper}
                onChange={(e) => onFieldChange('secondaryDeveloper', e.target.value)}
                required
              >
                <option value="">Select user</option>
                {activeAssignableUsers.map((entry) => (
                  <option key={`dashboard-secondary-${entry.user_email}`} value={entry.user_email}>
                    {entry.user_email}
                  </option>
                ))}
              </select>
            </label>
            {/* ── User Manager Admin — optional, only shows users with tableau_admin role ── */}
            <label>
              Dashboard User Manager Admin
              <select
                value={form.userManagerAdmin}
                onChange={(e) => onFieldChange('userManagerAdmin', e.target.value)}
              >
                <option value="">Select User Manager Admin</option>
                {activeDashboardUserManagerAdminUsers.map((entry) => (
                  <option key={`dashboard-user-manager-admin-${entry.user_email}`} value={entry.user_email}>
                    {entry.user_email}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit">{form.dashboardId ? 'Update Dashboard' : 'Create Dashboard'}</button>
          </form>
        </div>
      </div>

      {/* ── Configured Dashboards Table ── */}
      <div className="console-card">
        <h2>Configured Dashboards</h2>
        {loadingDashboards ? <p>Loading dashboards...</p> : null}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Primary</th>
                <th>Secondary</th>
                <th>Status</th>
                <th>Approval</th>
                <th>Deleted</th>
                <th>Updated By</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dashboards.map((row) => (
                <tr key={row.dashboard_id}>
                  <td>{row.name}</td>
                  <td>{row.primary_developer_email || '-'}</td>
                  <td>{row.secondary_developer_email || '-'}</td>
                  <td>{row.status || '-'}</td>
                  <td>{row.approval_status || 'draft'}</td>
                  <td>{row.is_deleted ? 'Yes' : 'No'}</td>
                  <td>{row.updated_by || '-'}</td>
                  <td>
                    <button type="button" onClick={() => loadDashboardIntoForm(row)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleToggleDashboardSoftDelete(row)}
                      disabled={dashboardDeleteLoadingId === row.dashboard_id}
                    >
                      {dashboardDeleteLoadingId === row.dashboard_id
                        ? 'Saving...'
                        : row.is_deleted
                          ? 'Restore'
                          : 'Soft Delete'}
                    </button>
                  </td>
                </tr>
              ))}
              {!dashboards.length && !loadingDashboards ? (
                <tr>
                  <td colSpan={8}>No dashboard assignments yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default DashboardManagementSection;